import type { UniqueIdentifier } from '@dnd-kit/core';
import { useDroppable } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { useMergeRefs } from '@floating-ui/react';
import type { ReactElement, Ref } from 'react';

import { clstx } from '@payaca/utilities/cvaMerge';
import DndContext from '../dragAndDrop/DndContext';
import SortableActiveItemOverlay from './SortableActiveItemOverlay';
import SortableItem from './SortableItem';
import useOptimisticReorder from './useOptimisticReorder';

interface BaseItem {
  id: UniqueIdentifier;
}

type Props<T extends BaseItem> = {
  items: T[];
  // Return `false` to reject the drop (e.g. failed validation); the rendered
  // order will snap back to `items`. Anything else (incl. void) commits.
  onDragEnd: (
    activeItem: T,
    destinationIndex: number,
    items: T[]
  ) => boolean | void;
  renderItem: (item: T) => ReactElement;
  renderDragOverlay?: (item: T) => ReactElement;
  id: string;
  label?: string;
  labelledBy?: string;
  isDisabled?: boolean;
  className?: string;
  lockAxis?: boolean;
  hasKeyboardSensor?: boolean;
  ref?: Ref<HTMLUListElement>;
  emptyState?: ReactElement;
};

const SortableList = <T extends BaseItem>(props: Props<T>) => {
  const {
    items,
    id,
    label,
    labelledBy,
    isDisabled = false,
    lockAxis = false,
    hasKeyboardSensor,
    className,
    renderItem,
    renderDragOverlay,
    ref,
    emptyState,
  } = props;
  const { setNodeRef } = useDroppable({ id });
  const mergedRef = useMergeRefs([setNodeRef, ref]);

  const { displayItems, commit } = useOptimisticReorder(items);

  return (
    <DndContext
      sortable
      hasKeyboardSensor={hasKeyboardSensor}
      modifiers={lockAxis ? [restrictToVerticalAxis] : undefined}
      onDragEnd={({ active, over }) => {
        if (!over || active.id === over.id) return;
        const activeIndex = displayItems.findIndex(
          ({ id }) => id === active.id
        );
        const overIndex = displayItems.findIndex(({ id }) => id === over.id);
        const newItems = arrayMove(displayItems, activeIndex, overIndex);
        const result = props.onDragEnd(
          displayItems[activeIndex],
          overIndex,
          newItems
        );
        if (result !== false) commit(newItems);
      }}
    >
      <SortableContext items={displayItems} strategy={rectSortingStrategy}>
        {displayItems.length === 0 && emptyState ? (
          emptyState
        ) : (
          <ul
            ref={mergedRef}
            className={clstx('m-0 p-0 list-none', className)}
            aria-label={label}
            aria-labelledby={labelledBy}
          >
            {displayItems.map((item) => (
              <SortableItem key={item.id} id={item.id} isDisabled={isDisabled}>
                {renderItem(item)}
              </SortableItem>
            ))}
          </ul>
        )}
      </SortableContext>

      <SortableActiveItemOverlay
        items={displayItems}
        renderItem={renderDragOverlay ?? renderItem}
      />
    </DndContext>
  );
};

export default SortableList;
